// THE OPERATIONS INBOX'S WEEKLY REPORT AND ONBOARDING CARDS, AS PURE READINGS:
// what a weekly-report or an onboarding-section card owes, from the facts the
// pages themselves read. No database: lib/server/ops-inbox-reports.ts reads
// the facts, and the tick hands each card's reading to `stepOneOff` (the
// report) or `stepOwed` (a section) in lib/ops-inbox-cards.ts.
//
// ⚠️ THE PAGES' OWN SIGNALS, NEVER A RULE OF OUR OWN. A report's state is the
// one `reportState` (lib/weekly-report.ts) paints on the weekly list, kept on
// the card as its inputs so the page paints the same chip; it is done when its
// week has a Final. A section's items, their readiness and their due dates are
// the checklist page's (getClientTree): an item is available when the page's
// Available filter would show it, and is due when the page says it is.
//
// Shared, not server-only, and erasable TypeScript: the tick runs in a sidecar
// under node's type stripping.

import type { CardDetail, CardRow, OneOffReading, OwedReading } from './ops-inbox-cards';
import { addDaysUtc, dowOf, formatShort } from './schedule-dates';
import { operationsDay } from './throughput-operations';
import { reportState, type ReportStateInput } from './weekly-report';

// ─── The weekly report ──────────────────────────────────────────────────────

/** One brand's report for one week, as the weekly list reads it, and its Final in force. */
export interface ReportFacts {
  /** The report week's first and last days (the brand's own week, not the app's Sunday one). */
  week: string;
  weekEnd: string;
  /** Somebody has written to it. */
  stored: boolean;
  /** Its newest version is a Delete. */
  deleted: boolean;
  /** A closed week older than the brand's latest closed one. */
  past: boolean;
  /** The Final in force: its number, who marked it and when, and the changes since. Null: never published. */
  final: { n: number; by: string; at: number; unpublished: number } | null;
}

/**
 * THE DAY A WEEK'S REPORT IS DUE: the first `dueWeekday` (0 Sunday … 6
 * Saturday) after the week closes. A Monday-to-Sunday week with the default
 * Monday is due the day after it ends; a Friday-to-Thursday one, the Monday
 * after it ends.
 */
export function reportDue(weekEnd: string, dueWeekday: number): string {
  const first = addDaysUtc(weekEnd, 1);
  return addDaysUtc(first, (((dueWeekday - dowOf(first)) % 7) + 7) % 7);
}

/** The report's state as `reportState` reads it, back from the card's `detail_json`. */
export function reportStateOf(detail: CardDetail | null): ReportStateInput {
  const n = (k: string): number | null => {
    const v = detail?.[k];
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  };
  return {
    running: false,
    stored: n('stored') === 1,
    deleted: n('deleted') === 1,
    finalN: n('finalN'),
    unpublished: n('unpublished') ?? 0,
    past: n('past') === 1,
  };
}

/**
 * A WEEKLY REPORT CARD'S READING: done when its week has a Final, explained
 * by whoever marked it ("Marked Final by Dana on Nov 5"). `detail_json` keeps
 * the inputs of `reportState`, so the card's chip is the list's own.
 *
 *   week / weekEnd                           the report week
 *   stored / deleted / past (0 or 1), finalN, unpublished   the state's inputs
 *
 * `nameOf` gives a person's first name for the history line.
 */
export function reportReading(f: ReportFacts, nameOf: (email: string) => string): OneOffReading {
  const extra: CardDetail = {
    week: f.week,
    weekEnd: f.weekEnd,
    stored: f.stored ? 1 : 0,
    deleted: f.deleted ? 1 : 0,
    past: f.past ? 1 : 0,
    finalN: f.final?.n ?? null,
    unpublished: f.final?.unpublished ?? 0,
  };
  const state = reportState(reportStateOf(extra)).label;
  const week = `the week of ${formatShort(f.week)}`;
  return {
    done: f.final === null ? null : { actor: f.final.by, at: f.final.at, via: 'app' },
    extra,
    notes: {
      made: `opened: ${week}'s report is ${state}`,
      done: f.final === null ? undefined : `closed: marked Final v${f.final.n} by ${nameOf(f.final.by)}`,
    },
  };
}

// ─── An onboarding section ──────────────────────────────────────────────────

/** One item of a checklist section, as the checklist page reads it. */
export interface SectionItem {
  id: number;
  checked: boolean;
  /** The page's readiness: done, blocked, available or locked. */
  readiness: string;
  /** When the page says it is due; null for an item with no date yet. */
  dueOn: string | null;
  /** When it last became available (the readiness log's `since`), for the archive rule. Null: not available. */
  availableSince: number | null;
}

/** One section of a client's checklist. */
export interface SectionFacts {
  /** `sectionKeys`' key (lib/first30-section-key.ts): what `?section=` and the card's subject name. */
  key: string;
  title: string;
  dayLabel: string | null;
  items: readonly SectionItem[];
}

/** Who ticked the last of a section's items, and when; their first name for the history line. */
export type LastTick = { actor: string; at: number; name: string } | null;

/** Why a section card closed for good, kept on it (`detail_json.closed`) so the page can say it. */
export type SectionClose = 'finished' | 'removed';

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const latest = (days: readonly (string | null)[]): string | null =>
  days.reduce<string | null>((m, d) => (d !== null && (m === null || d > m) ? d : m), null);

/**
 * AN ONBOARDING SECTION CARD'S READING (`stepOwed`'s). A section gets its
 * card once any of its items is available, and keeps it while any item is not
 * ticked, available or not: the work owed is the section. Due on its latest
 * item's due date, so it goes late; with no item dated yet, it is due today.
 * Its counter is the items ticked of all of them. Done when every item is
 * ticked, explained by whoever ticked the last ("Section finished: Dana
 * ticked the last item on Nov 5"); a section gone from the checklist closes
 * as removed.
 *
 *   section / title / dayLabel   which section, in the page's words
 *   available                    how many items are available now
 *   closed / closedOn            why it closed for good, and the day
 *
 * `lastTick` is asked only when the card closes, not on each pass after.
 */
export function sectionReading(
  card: CardRow | null,
  section: SectionFacts | null,
  today: string,
  at: number,
  lastTick: (itemIds: number[]) => LastTick,
): OwedReading {
  // A card already closed this way keeps the day it closed: a pass re-reading it must not move it.
  const closedBefore = (why: SectionClose): string | null =>
    card?.state === 'done' && card.detail?.closed === why && typeof card.detail.closedOn === 'string' ? card.detail.closedOn : null;
  if (section === null) {
    const keep = card?.detail ?? {};
    return {
      owed: 0,
      dueOn: card?.dueOn ?? today,
      target: card?.target ?? null,
      doneN: card?.doneN ?? null,
      done: null,
      workSince: null,
      extra: { section: keep.section ?? null, title: keep.title ?? null, dayLabel: keep.dayLabel ?? null, available: 0, closed: 'removed' satisfies SectionClose, closedOn: closedBefore('removed') ?? operationsDay(at) },
      notes: { closed: 'closed: the section is no longer on the checklist' },
    };
  }
  const total = section.items.length;
  const ticked = section.items.filter((i) => i.checked).length;
  const open = section.items.filter((i) => !i.checked);
  const available = open.filter((i) => i.readiness === 'available');
  // A section with nothing available yet is not owed until something is; once its card exists, every open item is.
  const owed = card === null && available.length === 0 ? 0 : open.length;
  const workSince = available.reduce<number | null>((m, i) => (i.availableSince !== null && (m === null || i.availableSince > m) ? i.availableSince : m), null);
  const extra: CardDetail = { section: section.key, title: section.title, dayLabel: section.dayLabel, available: available.length };
  const dueOn = latest(section.items.map((i) => i.dueOn)) ?? today;
  if (owed === 0 && card !== null) {
    const kept = closedBefore('finished');
    const by = kept === null && total > 0 ? lastTick(section.items.map((i) => i.id)) : null;
    return {
      owed: 0,
      dueOn,
      target: total,
      doneN: ticked,
      done: by === null ? null : { actor: by.actor, at: by.at, via: 'app' },
      workSince: null,
      extra: { ...extra, closed: 'finished' satisfies SectionClose, closedOn: kept ?? operationsDay(by?.at ?? at) },
      notes: { closed: by === null ? 'closed: every item is ticked' : `closed: section finished, ${by.name} ticked the last item` },
    };
  }
  return {
    owed,
    dueOn,
    target: total,
    doneN: ticked,
    done: null,
    workSince,
    extra,
    notes: {
      made: `opened: ${available.length} of ${plural(total, 'item')} available`,
      reopened: `reopened: ${plural(open.length, 'item')} not ticked`,
      back: `${plural(available.length, 'item')} available`,
    },
  };
}
